/* eslint-disable */
import React, { useEffect, useState } from "react";
import {
  Box,
  TextInput,
  SegmentedControl,
  ScrollArea,
  Stack,
  Avatar,
  Group,
  Text,
  Badge,
  Indicator,
} from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import SearchModal from "../search/search";
import { CrossOff, Plus } from "tabler-icons-react";
import { textOverlap } from "utils/commonFunction";

type Conversation = {
  id: string;
  name: string;
  last: string;
  time: string;
  unread: number;
  status: "online" | "away" | "offline";
  type: "direct" | "group";
  otherUserId?: string;
};

export default function ListingMessage({
  visibleConversations,
  search,
  setSearch,
  segValue,
  setSegValue,
  emitRead,
  selectedConversationId,
  setSelectedConversationId,
  classes,
  cx,
  defaultConversationId,
  isUserOnline,
  onlineUsers,
}: {
  visibleConversations: Conversation[];
  search: string;
  emitRead?: (payload: any) => void;
  setSearch: (v: string) => void;
  segValue: "all" | "direct" | "group";
  setSegValue: (v: "all" | "direct" | "group") => void;
  selectedConversationId: string;
  setSelectedConversationId: (id: string) => void;
  classes: Record<string, string> & { activeConvo?: string };
  cx: (...args: any[]) => string;
  defaultConversationId?: string;
  isUserOnline: any;
  onlineUsers: any;
}) {
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    if (!selectedConversationId) {
      if (defaultConversationId) {
        setSelectedConversationId(defaultConversationId);
      } else if (visibleConversations && visibleConversations.length > 0) {
        setSelectedConversationId(visibleConversations[0].id);
      }
    }
  }, [
    visibleConversations,
    selectedConversationId,
    defaultConversationId,
    setSelectedConversationId,
  ]);

  function getInitials(name?: string) {
    if (!name) return "NA";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  function avatarColorFromId(id: string) {
    const colors: any[] = ["blue", "violet", "teal", "orange", "red", "green"];
    let h = 0;
    for (let i = 0; i < id.length; i++) {
      h = (h << 5) - h + id.charCodeAt(i);
      h |= 0;
    }
    return colors[Math.abs(h) % colors.length];
  }

  return (
    <Box className={classes.leftPanel}>
      <Box
        className={classes.gradientPanel}
        style={{ borderRadius: 8, padding: 12, marginBottom: 14 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <Text weight={700} color="#fff" size="lg">
              Messages
            </Text>
            <Text color="#e6e6ff" size="xs" style={{ marginTop: 6 }}>
              Search conversations
            </Text>
          </div>
          <span onClick={() => setOpened(true)}>
            <Plus color="white" />
          </span>
        </div>
      </Box>

      <TextInput
        icon={<IconSearch size={16} />}
        placeholder="Search conversations..."
        variant="filled"
        className={classes.searchBox}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />

      <SegmentedControl
        data={[
          { label: "All", value: "all" },
          { label: "Direct", value: "direct" },
          { label: "Groups", value: "group" },
        ]}
        fullWidth
        mb="sm"
        value={segValue}
        onChange={(v) => setSegValue(v as "all" | "direct" | "group")}
      />

      <ScrollArea style={{ height: "calc(100% - 230px)" }}>
        <Stack spacing={6}>
          {visibleConversations.length === 0 && (
            <Box px="sm" py="md">
              <Text color="dimmed" size="sm">
                No conversations found.
              </Text>
            </Box>
          )}

          {visibleConversations.map((c) => {
            const isActive = c.id === selectedConversationId;
            const online =
              Array.isArray(onlineUsers) && c.otherUserId
                ? onlineUsers.includes(String(c.otherUserId))
                : typeof isUserOnline === "function"
                ? isUserOnline(c.otherUserId)
                : false;
            // console.log(`Conversation ${c.id} online:`, online);
            return (
              <Box
                key={c.id}
                className={cx(classes.convoItem, {
                  [classes.activeConvo || ""]: isActive,
                })}
                onClick={() => setSelectedConversationId(c.id)}
                role="button"
                aria-pressed={isActive}
                sx={{ position: "relative" }}
              >
                <span
                  style={{
                    position: "absolute",
                    top: 8,
                    left: 12,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: 9,
                    zIndex: 2,
                  }}
                  aria-label={online ? "online" : "offline"}
                >
                  {online ? (
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 6,
                        backgroundColor: "#2ecc71",
                        display: "block",
                      }}
                    />
                  ) : (
                    <CrossOff size={14} color="black" aria-hidden={false} />
                  )}
                </span>
                <Avatar
                  color={avatarColorFromId(c.id)}
                  radius="xl"
                  title={c.name}
                >
                  {getInitials(c.name)}
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Group position="apart">
                    <Text weight={700}>{c.name}</Text>

                    <Text size="xs" color="dimmed">
                      {c.time}
                    </Text>
                  </Group>
                  <Group position="apart" mt={4}>
                    <Text
                      size="sm"
                      color="dimmed"
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {textOverlap(c.last, 20)}
                      {/* {c.last} */}
                    </Text>
                    {c.unread ? (
                      <Badge color="red" variant="filled">
                        {c.unread}
                      </Badge>
                    ) : null}
                  </Group>
                </div>
              </Box>
            );
          })}
        </Stack>
      </ScrollArea>
      <SearchModal opened={opened} onClose={() => setOpened(false)} />
    </Box>
  );
}
